package org.multiplatform.kotfolio.pages.compose

import androidx.compose.runtime.Composable
import org.multiplatform.kotfolio.components.KotlinCode
import org.multiplatform.kotfolio.layout.BlogLayout
import org.multiplatform.kotfolio.layout.BlogProperties
import org.multiplatform.kotfolio.layout.PageProperties
import com.varabyte.kobweb.compose.css.FontSize
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText

import org.jetbrains.compose.web.css.em
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Ul


@Page("/compose/compose_style_api")
@Composable
fun StyleApiComposePage() {
    BlogLayout(
        PageProperties(
            "Style API",
            ""
        ),
        BlogProperties("Jetpack Compose", "Style API")
    ) {
        Column(
            horizontalAlignment = Alignment.Start
        ) {
            
            SpanText(
                text = "Style",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Style interface is the foundation of the API. It's defined as a functional interface that operates on a StyleScope.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "A StyleScope is the receiver scope of a Style lambda. It allows defining the properties of a style that will be used by a styleable modifier to allow customizing the look of a composable component in its default state and in states like hover, pressed, or focused readable from the state property. The properties set on a style scope affect the styled region of a component. A component creates a styled region by using the styleable modifier.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    @ExperimentalFoundationStyleApi
                    fun interface Style {
                        fun StyleScope.applyStyle()

                        companion object : Style {
                            @Suppress("MissingJvmstatic") override fun StyleScope.applyStyle() {}
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    val style = Style {
                        background(defaultColor)
                        shape(RoundedCornerShape(16.dp))
                        pressed(Style { animate(Style { background(pressedColor); scale(0.95f) }) })
                    }
                    Box(Modifier.styleable(style = style))
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    val baseStyle = Style {
                        background(Color.White)
                        contentPadding(16.dp)
                    }

                    val borderedStyle = Style {
                        borderWidth(1.dp)
                        borderColor(Color.Gray)
                    }

                    val combinedStyle = baseStyle then borderedStyle
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    @OptIn(ExperimentalFoundationStyleApi::class)
                    @Composable
                    private fun SelectableCard(
                        label: String,
                        isSelected: Boolean,
                        onSelect: () -> Unit,
                        modifier: Modifier = Modifier,
                    ) {
                        val interactionSource = remember { MutableInteractionSource() }
                        val styleState = remember { MutableStyleState(interactionSource) }
                        styleState.isSelected = isSelected

                        val cardStyle = Style {
                            background(Color(0xFFF5F5F5))
                            shape(RoundedCornerShape(12.dp))
                            contentPadding(16.dp)
                            selected(Style {
                                animate(Style {
                                    background(AccentBlue.copy(alpha = 0.15f))
                                    borderWidth(2.dp)
                                    borderColor(AccentBlue)
                                })
                            })
                            pressed(Style {
                                animate(Style {
                                    scale(0.95f)
                                })
                            })
                        }

                        Box(
                            modifier = modifier
                                .styleable(styleState = styleState, style = cardStyle)
                                .clickable(
                                    interactionSource = interactionSource,
                                    indication = null,
                                ) { onSelect() },
                            contentAlignment = Alignment.Center,
                        ) {
                            Column(horizontalAlignment = Alignment.CenterHorizontally) {
                                Text(
                                    text = label,
                                    style = MaterialTheme.typography.bodyMedium,
                                    color = if (isSelected) AccentBlue else Color(0xFF616161),
                                )
                                if (isSelected) {
                                    Spacer(modifier = Modifier.height(4.dp))
                                    Box(
                                        modifier = Modifier.size(8.dp),
                                    ) {
                                        Icon(
                                            imageVector = Icons.Filled.Check,
                                            contentDescription = "Selected",
                                            tint = AccentBlue,
                                            modifier = Modifier.size(8.dp),
                                        )
                                    }
                                }
                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    @OptIn(ExperimentalFoundationStyleApi::class)
                    object StyledChipDefaults {
                        @Composable
                        fun style(): Style {
                            val bg = MaterialTheme.colorScheme.secondaryContainer
                            val fg = MaterialTheme.colorScheme.onSecondaryContainer
                            return Style {
                                background(bg)
                                shape(RoundedCornerShape(8.dp))
                                contentPadding(horizontal = 16.dp, vertical = 8.dp)
                                contentColor(fg)
                                pressed(Style {
                                    animate(Style {
                                        scale(0.95f)
                                    })
                                })
                            }
                        }
                    }

                    @OptIn(ExperimentalFoundationStyleApi::class)
                    @Composable
                    fun StyledChip(
                        onClick: () -> Unit,
                        modifier: Modifier = Modifier,
                        style: Style = StyledChipDefaults.style(),
                        content: @Composable () -> Unit,
                    ) {
                        val interactionSource = remember { MutableInteractionSource() }
                        val styleState = remember { MutableStyleState(interactionSource) }

                        Box(
                            modifier = modifier
                                .styleable(styleState = styleState, style = style)
                                .clickable(
                                    interactionSource = interactionSource,
                                    indication = null,
                                    onClick = onClick,
                                ),
                            contentAlignment = Alignment.Center,
                        ) {
                            content()
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    @OptIn(ExperimentalFoundationStyleApi::class)
                    @Composable
                    fun StyleCompositionLab(onBack: () -> Unit) {
                        var baseEnabled by remember { mutableStateOf(true) }
                        var elevatedEnabled by remember { mutableStateOf(false) }
                        var darkEnabled by remember { mutableStateOf(false) }

                        val baseCard = Style {
                            background(LabCyan.copy(alpha = 0.15f))
                            shape(RoundedCornerShape(16.dp))
                            contentPadding(horizontal = 24.dp, vertical = 20.dp)
                        }

                        val elevatedCard = Style {
                            borderWidth(2.dp)
                            borderColor(Color(0xFFB0BEC5))
                            scale(1.02f)
                        }

                        val darkTheme = Style {
                            background(Color(0xFF1E1E2E))
                            contentColor(Color.White)
                        }

                        val composedStyle = remember(baseEnabled, elevatedEnabled, darkEnabled) {
                            var result: Style = Style {}
                            if (baseEnabled) result = result.then(baseCard)
                            if (elevatedEnabled) result = result.then(elevatedCard)
                            if (darkEnabled) result = result.then(darkTheme)
                            result
                        }
                        
                        .styleable(
                            styleState = previewStyleState,
                                style = composedStyle.then(
                                    Style {
                                        pressed(Style {
                                            animate(Style { scale(0.97f) })
                                        })
                                    }
                                ),
                        )
                    }

                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    @OptIn(ExperimentalFoundationStyleApi::class)
                    @Composable
                    private fun BouncyPressButton(modifier: Modifier = Modifier) {
                        val interactionSource = remember { MutableInteractionSource() }
                        val styleState = remember { MutableStyleState(interactionSource) }

                        val bouncyStyle = Style {
                            background(Color(0xFFFF6D00))
                            shape(RoundedCornerShape(16.dp))
                            contentPadding(24.dp)
                            pressed(Style {
                                animate(Style {
                                    scale(0.85f)
                                })
                            })
                        }

                        Box(
                            modifier = modifier
                                .fillMaxWidth()
                                .styleable(styleState = styleState, style = bouncyStyle)
                                .clickable(interactionSource = interactionSource, indication = null) { },
                            contentAlignment = Alignment.Center,
                        ) {
                            Column(horizontalAlignment = Alignment.CenterHorizontally) {
                                Text(
                                    text = "Press & Hold",
                                    style = MaterialTheme.typography.titleMedium,
                                    color = Color.White,
                                )
                                Text(
                                    text = "Scales to 0.85x on press",
                                    style = MaterialTheme.typography.bodySmall,
                                    color = Color.White.copy(alpha = 0.8f),
                                )
                            }
                        }
                    }
                    """.trimIndent()
            )
        }
    }
}

//https://github.com/aldefy/compose-style-lab
//https://github.com/aldefy/compose-style-lab?ref=aditlal.dev
//https://github.com/ardakazanci/JetpackCompose-New-Style-API

//https://aditlal.dev/compose-styles